import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';

/**
 * Google sign-in button, shared by Login and Register.
 *
 * The public client ID comes from the backend (`GET /auth/config`) so nothing
 * needs to be wired into the frontend .env - configure once in the backend's
 * oauth.config.json and the button lights up. Until an ID is present the button
 * stays disabled with a hint, so the UI never silently does nothing.
 *
 *  - Google: the official Identity Services button returns a signed credential
 *    (ID token); we post it to /auth/google.
 *
 * On success the parent decides where to go (onAuthed); a 2FA-protected account
 * hands back a challenge instead (onTwoFactor).
 */

declare global {
  interface Window {
    google?: any;
  }
}

interface Props {
  onAuthed: (user: any, accessToken: string) => void;
  onTwoFactor?: (challenge: string) => void;
}

interface PublicConfig {
  googleClientId: string | null;
}

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.id = id;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

export function SocialAuthButtons({ onAuthed, onTwoFactor }: Props) {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [busy, setBusy] = useState<'google' | null>(null);
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [btnWidth, setBtnWidth] = useState(0);

  // Pull the public client ID once.
  useEffect(() => {
    let alive = true;
    api.get<PublicConfig>('/auth/config')
      .then(({ data }) => { if (alive) setConfig(data); })
      .catch(() => { if (alive) setConfig({ googleClientId: null }); });
    return () => { alive = false; };
  }, []);

  function handleResult(data: any) {
    if (data?.twoFactorRequired) {
      if (onTwoFactor) onTwoFactor(data.challenge);
      else toast('Please finish logging in with your 2FA code.');
      return;
    }
    onAuthed(data.user, data.accessToken);
  }

  // Track the container width so the Google button matches the full-width
  // "Create account" button and stays responsive on resize (GSI needs a px width).
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setBtnWidth(Math.round(el.getBoundingClientRect().width));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [config?.googleClientId]);

  // Render the official Google button once we have a client ID.
  useEffect(() => {
    if (!config?.googleClientId || !googleBtnRef.current) return;
    let alive = true;
    loadScript('https://accounts.google.com/gsi/client', 'gsi-client')
      .then(() => {
        if (!alive || !window.google?.accounts?.id || !googleBtnRef.current) return;
        window.google.accounts.id.initialize({
          client_id: config.googleClientId,
          ux_mode: 'popup',
          callback: async (resp: any) => {
            setBusy('google');
            try {
              const { data } = await api.post('/auth/google', { credential: resp.credential });
              handleResult(data);
            } catch (e: any) {
              toast.error(e?.response?.data?.message ?? 'Google sign-in failed. Please try again.');
            } finally {
              setBusy(null);
            }
          },
        });
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          width: Math.max(220, Math.min(400, Math.round((btnWidth || 320) / 1.2))),
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'center',
        });
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.googleClientId, btnWidth]);

  return (
    <div ref={wrapRef} className="space-y-2.5">
      {config?.googleClientId
        ? <div ref={googleBtnRef} className="h-[50px] rounded-luxe border border-ink-600 overflow-hidden flex items-center justify-center [&>div]:origin-center [&>div]:[transform:scale(1.2)]" aria-busy={busy === 'google'} />
        : (
          <button type="button" disabled className="btn btn-outline btn-lg w-full opacity-60 cursor-not-allowed"
            title="Set googleClientId in the backend oauth.config.json to enable">
            <span className="font-semibold mr-1.5">G</span> Continue with Google
          </button>
        )}
    </div>
  );
}
